import * as THREE from 'three';
import { FOCUS_HUD_ORBIT_DIST, NEST_Z_STEP } from '../constants';
import {
  computeBestCameraForNode,
  distanceToFrameBounds,
  frameLookShift,
  gatherOverviewBounds,
  gatherOverviewScreenExtents,
  overviewFrameInsets,
  shiftTargetToEqualScreenAir,
} from '../utils/neuralSceneUtils';
import { distanceToContainIdleOrbit } from './runNeuralAnimationFrame/idleDrift';

const _overviewCam = new THREE.PerspectiveCamera();
/** Air around the completed mission and whatever it just unlocked. */
const IGNITION_FRAME_PADDING = 1.35;

/** Overview / HUD camera goals. */
export function createCameraFraming({
  camera,
  controls,
  container,
  graphRef,
  meshByTaskId,
  labelByTaskId,
  nebula,
  viewDir,
  MIN_DIST,
  MAX_DIST,
  INITIAL_DIST,
  targetDistRef,
  focusTargetGoalRef,
  focusCameraGoalRef,
  driftRef,
  motion,
  pauseIdle,
}) {
  let overviewDistance = INITIAL_DIST;

  function liveContentBounds(fromCamera = camera) {
    return gatherOverviewBounds({
      camera: fromCamera,
      container,
      meshByTaskId,
      labelByTaskId,
      nebula,
      fallback: graphRef.current?.bounds,
    });
  }

  function contentCenter(bounds) {
    if (!bounds) return new THREE.Vector3();
    return new THREE.Vector3((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2, 0);
  }

  function poseOverviewCam(target, dist) {
    _overviewCam.fov = camera.fov;
    _overviewCam.aspect = Math.max(camera.aspect, 0.2);
    _overviewCam.near = camera.near;
    _overviewCam.far = camera.far;
    _overviewCam.updateProjectionMatrix();
    _overviewCam.position.copy(target).addScaledVector(viewDir, dist);
    _overviewCam.lookAt(target);
    _overviewCam.updateMatrixWorld(true);
    return _overviewCam;
  }

  function placeOverview(seedBounds) {
    const insets = overviewFrameInsets(container);
    let bounds = seedBounds || liveContentBounds();
    const target = contentCenter(bounds);
    let dist = distanceToFrameBounds(bounds, camera.fov, camera.aspect, 1, insets);
    target.y += frameLookShift(dist, camera.fov, camera.aspect, { top: insets.top }).y;

    for (let i = 0; i < 3; i += 1) {
      if (!motion.reduced) {
        dist = distanceToContainIdleOrbit({
          bounds,
          fovDeg: camera.fov,
          aspect: camera.aspect,
          viewDir,
          target,
          insets,
          padding: 1,
        });
        target.y = contentCenter(bounds).y
          + frameLookShift(dist, camera.fov, camera.aspect, { top: insets.top }).y;
      }
      const scratch = poseOverviewCam(target, dist);
      bounds = liveContentBounds(scratch);
      const screen = gatherOverviewScreenExtents({
        camera: scratch,
        container,
        meshByTaskId,
        labelByTaskId,
        nebula,
      });
      shiftTargetToEqualScreenAir(scratch, target, screen);
    }

    return { target, dist: THREE.MathUtils.clamp(dist, 8, MAX_DIST) };
  }

  function syncOverviewDistance() {
    const placed = placeOverview(liveContentBounds());
    overviewDistance = placed.dist;
    return overviewDistance;
  }

  function applyCameraGoal(target, dist, opts = {}) {
    const clamped = THREE.MathUtils.clamp(dist, MIN_DIST, MAX_DIST);
    focusTargetGoalRef.v = target;
    focusCameraGoalRef.v = target.clone().addScaledVector(viewDir, clamped);
    targetDistRef.v = clamped;
    driftRef.fresh = true;
    if (motion.reduced || opts.snap) {
      camera.position.copy(focusCameraGoalRef.v);
      controls.target.copy(focusTargetGoalRef.v);
      camera.lookAt(controls.target);
      camera.updateMatrixWorld(true);
      focusTargetGoalRef.v = focusCameraGoalRef.v = null;
    }
  }

  function frameOverview(event = null, opts = {}) {
    if (event) {
      frameIgnition(event, opts);
      return;
    }
    const placed = placeOverview(liveContentBounds());
    overviewDistance = placed.dist;
    applyCameraGoal(placed.target, placed.dist, opts);
  }

  /** Zoom out (or re-center) so a completion and everything it unlocked stay in frame. */
  function frameIgnition(event, opts = {}) {
    const ids = [...(event?.sourceIds || []), ...(event?.targetIds || [])];
    if (!ids.some((id) => {
      const mesh = meshByTaskId.get(id);
      return mesh?.visible && !mesh.userData?.exiting;
    })) return;
    const bounds = gatherOverviewBounds({
      camera,
      container,
      meshByTaskId,
      labelByTaskId,
      nebula: null,
      taskIds: ids,
      includeHiddenLabels: true,
    });
    if (!Number.isFinite(bounds?.minX)) return;
    const insets = overviewFrameInsets(container);
    const target = contentCenter(bounds);
    const dist = distanceToFrameBounds(bounds, camera.fov, camera.aspect, IGNITION_FRAME_PADDING, insets);
    target.y += frameLookShift(dist, camera.fov, camera.aspect, { top: insets.top }).y;
    pauseIdle({ syncDist: false });
    applyCameraGoal(target, dist, opts);
  }

  function focusMission(taskId) {
    if (!taskId) return;
    const subject = meshByTaskId.get(taskId);
    if (!subject || subject.userData.exiting || !subject.visible) return;
    const w = subject.position.clone();
    const nestCount = subject.userData.nodeMeta?.nestCount || 0;
    const orbit = FOCUS_HUD_ORBIT_DIST + Math.sqrt(nestCount) * 1.6 + (nestCount ? NEST_Z_STEP * 0.35 : 0);
    if (nestCount) w.z -= NEST_Z_STEP * 0.4;
    const bestCam = computeBestCameraForNode(w, orbit);
    focusTargetGoalRef.v = w.clone();
    focusCameraGoalRef.v = bestCam.clone();
    targetDistRef.v = THREE.MathUtils.clamp(bestCam.distanceTo(w), MIN_DIST, MAX_DIST);
    pauseIdle();
    if (motion.reduced) {
      camera.position.copy(bestCam);
      controls.target.copy(w);
      focusTargetGoalRef.v = focusCameraGoalRef.v = null;
    }
  }

  return { frameOverview, frameIgnition, focusMission, syncOverviewDistance };
}
